import { BaseWebComponentDataProvider } from '../../../framework/BaseWebComponentDataProvider';
import { DEFAULT_PAGE_NUMBER, ViewConfiguration, ViewType } from '../../../grid/gridView/types';
import {
  ACCOUNT_LOCKOUT,
  AccountsGridViewData,
  ColumnIndexes,
  ColumnKeys,
  DEFAULT_PAGE_SIZE,
  FilterAvailableServiceOption,
  PASSWORD_EXPIRATION,
  serviceTypes,
  STATUS,
  TWO_FA,
} from '../types';
import { Filter } from '../../../grid/gridFilters/types';
import { ColumnDetails, ColumnType } from '../../../grid/types/common';
import { translateFn } from '../../../utils/translator.helper';

export class AccountsGridBaseDataProvider extends BaseWebComponentDataProvider {
  async getDataForView(): Promise<AccountsGridViewData> {
    const view: ViewConfiguration<ColumnIndexes, ColumnKeys> = this.getView();
    return {
      views: [view],
      allColumns: this.getAllColumns(),
      defaultViewProps: view,
      predefinedFilterKeys: this.getPredefinedFiltersKeys(),
    };
  }

  private getView(): ViewConfiguration<ColumnIndexes, ColumnKeys> {
    return {
      id: 'accounts-grid',
      name: 'Default view',
      type: ViewType.DEFAULT,
      filters: this.getDefaultFilters(),
      displayedColumns: this.getDisplayedColumns(),
      pageSize: DEFAULT_PAGE_SIZE,
      pageNumber: DEFAULT_PAGE_NUMBER,
      compactView: false,
    };
  }

  private getDefaultFilters(): Filter<ColumnKeys>[] {
    if (this.dependencies.isCloud()) {
      const filters = [
        { [ColumnKeys.FULL_NAME]: { contains: '' } },
        { [ColumnKeys.EMAIL]: { contains: '' } },
        { [ColumnKeys.ROLE]: { in: [] } },
        { [ColumnKeys.STATUS]: { in: [] } },
      ];
      if (this.dependencies.permissions.canHaveChildCompanies) {
        filters.push({ [ColumnKeys.COMPANY]: { in: [this.dependencies.getUser().company.id] } } as any);
      }
      return filters;
    }

    return [
      { [ColumnKeys.USERNAME]: { contains: '' } },
      { [ColumnKeys.EMAIL]: { contains: '' } },
      { [ColumnKeys.ROLE]: { in: [] } },
      { [ColumnKeys.SERVICES]: { in: [] } },
      { [ColumnKeys.STATUS]: { in: [] } },
    ];
  }

  private getDisplayedColumns(): Array<ColumnIndexes> {
    if (this.dependencies.isCloud()) {
      const columns = [
        ColumnIndexes.FULL_NAME,
        ColumnIndexes.EMAIL,
        ColumnIndexes.STATUS,
        ColumnIndexes.ROLE,
        ColumnIndexes.TWO_FA,
        ColumnIndexes.PASSWORD_EXPIRATION,
        ColumnIndexes.ACCOUNT_LOCKOUT,
      ];
      if (this.dependencies.permissions.canHaveChildCompanies) {
        columns.push(ColumnIndexes.COMPANY);
      }
      return columns;
    }

    return [
      ColumnIndexes.USERNAME,
      ColumnIndexes.EMAIL,
      ColumnIndexes.STATUS,
      ColumnIndexes.ROLE,
      ColumnIndexes.SERVICES,
      ColumnIndexes.TWO_FA,
      ColumnIndexes.ACCESS_RULE,
    ];
  }

  private getPredefinedFiltersKeys(): ColumnKeys[] {
    if (this.dependencies.isCloud()) {
      const filters = [ColumnKeys.FULL_NAME, ColumnKeys.EMAIL, ColumnKeys.STATUS, ColumnKeys.ROLE];
      if (this.dependencies.permissions.canHaveChildCompanies) {
        filters.push(ColumnKeys.COMPANY);
      }
      return filters;
    }

    return [ColumnKeys.USERNAME, ColumnKeys.EMAIL, ColumnKeys.STATUS, ColumnKeys.ROLE, ColumnKeys.SERVICES];
  }

  private getAllColumns(): ColumnDetails<ColumnIndexes, ColumnKeys>[] {
    const visibleRoles = this.dependencies.getVisibleRoles();

    if (this.dependencies.isCloud()) {
      const columns = [
        {
          column: ColumnIndexes.FULL_NAME,
          key: ColumnKeys.FULL_NAME,
          columnName: translateFn('Full Name'),
          type: ColumnType.STRING,
          removable: false,
          sortable: true,
        },
        {
          column: ColumnIndexes.EMAIL,
          key: ColumnKeys.EMAIL,
          columnName: translateFn('Email'),
          type: ColumnType.STRING,
          removable: false,
          sortable: true,
        },
        {
          column: ColumnIndexes.STATUS,
          key: ColumnKeys.STATUS,
          columnName: translateFn('Status'),
          type: ColumnType.ENUM,
          values: [
            {
              label: translateFn('Active'),
              value: STATUS.ACTIVE,
            },
            {
              label: translateFn('Suspended'),
              value: STATUS.SUSPENDED,
            },
          ],
          removable: true,
          sortable: false,
        },
        {
          column: ColumnIndexes.ROLE,
          key: ColumnKeys.ROLE,
          columnName: translateFn('Role'),
          type: ColumnType.ENUM,
          values: visibleRoles,
          removable: true,
          sortable: true,
        },
      ] as ColumnDetails<ColumnIndexes, ColumnKeys>[];
      if (this.dependencies.permissions.canHaveChildCompanies) {
        columns.push({
          column: ColumnIndexes.COMPANY,
          key: ColumnKeys.COMPANY,
          columnName: translateFn('Company'),
          type: ColumnType.COMPANY,
          removable: true,
          sortable: true,
          enumFilterCanSelectAll: true,
          showOnlySelectAll: false,
          filterResetValue: [this.dependencies.getUser().company.id],
        });
      }

      columns.push(
        {
          column: ColumnIndexes.TWO_FA,
          key: ColumnKeys.TWO_FA,
          columnName: translateFn('2FA'),
          type: ColumnType.ENUM,
          values: [
            {
              label: translateFn('Disabled'),
              value: TWO_FA.DISABLED,
            },
            {
              label: translateFn('Enabled'),
              value: TWO_FA.ENABLED,
            },
          ],
          sortable: true,
          removable: true,
        },
        {
          column: ColumnIndexes.PASSWORD_EXPIRATION,
          key: ColumnKeys.PASSWORD_EXPIRATION,
          columnName: translateFn('Password expiration'),
          type: ColumnType.ENUM,
          values: [
            {
              label: translateFn('Disabled'),
              value: PASSWORD_EXPIRATION.DISABLED,
            },
            {
              label: translateFn('Enabled'),
              value: PASSWORD_EXPIRATION.ENABLED,
            },
          ],
          sortable: true,
          removable: true,
        },
        {
          column: ColumnIndexes.ACCOUNT_LOCKOUT,
          key: ColumnKeys.ACCOUNT_LOCKOUT,
          columnName: translateFn('Account lockout'),
          type: ColumnType.ENUM,
          values: [
            {
              label: translateFn('Disabled'),
              value: ACCOUNT_LOCKOUT.DISABLED,
            },
            {
              label: translateFn('Enabled'),
              value: ACCOUNT_LOCKOUT.ENABLED,
            },
          ],
          sortable: true,
          removable: true,
        }
      );
      return columns;
    }

    return [
      {
        column: ColumnIndexes.USERNAME,
        key: ColumnKeys.USERNAME,
        columnName: translateFn('Username'),
        type: ColumnType.STRING,
        removable: false,
        sortable: true,
      },
      {
        column: ColumnIndexes.EMAIL,
        key: ColumnKeys.EMAIL,
        columnName: translateFn('Email'),
        type: ColumnType.STRING,
        removable: false,
        sortable: true,
      },
      {
        column: ColumnIndexes.STATUS,
        key: ColumnKeys.STATUS,
        columnName: translateFn('Status'),
        type: ColumnType.ENUM,
        values: [
          {
            label: translateFn('Active'),
            value: STATUS.ACTIVE,
          },
          {
            label: translateFn('Suspended'),
            value: STATUS.SUSPENDED,
          },
          {
            label: translateFn('AD integration removed'),
            value: STATUS.AD_INTEGRATION_REMOVED,
          },
        ],
        removable: true,
        sortable: false,
      },
      {
        column: ColumnIndexes.ROLE,
        key: ColumnKeys.ROLE,
        columnName: translateFn('Role'),
        type: ColumnType.ENUM,
        values: visibleRoles,
        removable: true,
        sortable: true,
      },
      {
        column: ColumnIndexes.SERVICES,
        key: ColumnKeys.SERVICES,
        columnName: translateFn('Services'),
        type: ColumnType.ENUM,
        values: this.getAvailableServices(),
        removable: true,
        sortable: false,
      },
      {
        column: ColumnIndexes.TWO_FA,
        key: ColumnKeys.TWO_FA,
        columnName: translateFn('2FA'),
        type: ColumnType.ENUM,
        values: [
          {
            label: translateFn('Disabled'),
            value: TWO_FA.DISABLED,
          },
          {
            label: translateFn('Enabled'),
            value: TWO_FA.ENABLED,
          },
        ],
        sortable: true,
        removable: true,
      },
      {
        column: ColumnIndexes.ACCESS_RULE,
        key: ColumnKeys.ACCESS_RULE,
        columnName: translateFn('Access Rule'),
        type: ColumnType.STRING,
        sortable: true,
        removable: true,
      },
    ];
  }

  private getAvailableServices() {
    if (!this.dependencies.getAvailableServices()) {
      return [];
    }

    const services = {
      [serviceTypes.EPS]: translateFn('Computers'),
      [serviceTypes.SVE]: translateFn('Cloud Workloads'),
      [serviceTypes.MDM]: translateFn('Mobile Devices'),
    };

    let options = [] as Array<FilterAvailableServiceOption>;
    this.dependencies.getAvailableServices().forEach(availableService => {
      options.push({
        label: services[availableService.id],
        value: availableService.id,
      });
    });
    return options;
  }
}
